import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/Button';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/contexts/ToastContext';
import { 
  confirmSignIn, 
  getCurrentUser 
} from 'aws-amplify/auth';

const MFAVerification = () => {
  const [verificationCode, setVerificationCode] = useState('');
  const [backupCode, setBackupCode] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [useBackupCode, setUseBackupCode] = useState(false);

  const { checkAuthState } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const location = useLocation();

  // Get the return URL from location state, or default to dashboard
  const from = location.state?.from?.pathname || '/dashboard';

  useEffect(() => {
    // Check if user is already authenticated (shouldn't happen, but safety check)
    checkAuthState();
  }, [checkAuthState]);

  const handleVerifyMFA = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setErrors({});

    const code = useBackupCode ? backupCode : verificationCode;
    
    // Validate code
    if (!code) {
      setErrors({ 
        [useBackupCode ? 'backupCode' : 'verificationCode']: 
        useBackupCode ? 'Please enter a backup code' : 'Please enter a 6-digit code' 
      });
      setIsLoading(false);
      return;
    }

    if (!useBackupCode && code.length !== 6) {
      setErrors({ verificationCode: 'Please enter a valid 6-digit code' });
      setIsLoading(false);
      return;
    }

    try {
      await confirmSignIn({
        challengeResponse: code
      });

      toast({
        title: 'Authentication Successful',
        description: 'You have been successfully logged in.',
        variant: 'success'
      });

      // Check auth state and redirect
      await checkAuthState();
      navigate(from, { replace: true });
    } catch (error) {
      console.error('MFA verification error:', error);
      
      let errorMessage = 'Invalid verification code. Please try again.';
      
      if (error.name === 'NotAuthorizedException') {
        errorMessage = 'Invalid code. Please check your authenticator app or backup code.';
      } else if (error.name === 'CodeMismatchException') {
        errorMessage = 'The code you entered is incorrect. Please try again.';
      } else if (error.name === 'ExpiredCodeException') {
        errorMessage = 'The code has expired. Please enter a new code from your authenticator app.';
      }

      setErrors({ 
        [useBackupCode ? 'backupCode' : 'verificationCode']: errorMessage 
      });

      toast({
        title: 'Verification Failed',
        description: errorMessage,
        variant: 'error'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendCode = () => {
    toast({
      title: 'Code Refreshed',
      description: 'Please enter the latest code from your authenticator app.',
      variant: 'info'
    });
  };

  const toggleCodeType = () => {
    setUseBackupCode(!useBackupCode);
    setVerificationCode('');
    setBackupCode('');
    setErrors({});
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
            Verify Your Identity
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600">
            Enter the code from your authenticator app to complete sign in
          </p>
        </div>

        <form onSubmit={handleVerifyMFA} className="space-y-6">
          <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-blue-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-blue-800">
                  Multi-Factor Authentication Required
                </h3>
                <div className="mt-2 text-sm text-blue-700">
                  <p>
                    For your security, please verify your identity using your authenticator app.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {!useBackupCode ? (
            <div>
              <label htmlFor="verificationCode" className="block text-sm font-medium text-gray-700">
                Enter 6-digit code from your authenticator app:
              </label>
              <input
                id="verificationCode"
                name="verificationCode"
                type="text"
                maxLength="6"
                value={verificationCode}
                onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, ''))}
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary focus:border-primary sm:text-sm text-center text-lg tracking-widest"
                placeholder="123456"
                autoComplete="off"
              />
              {errors.verificationCode && (
                <p className="mt-1 text-sm text-red-600">{errors.verificationCode}</p>
              )}
            </div>
          ) : (
            <div>
              <label htmlFor="backupCode" className="block text-sm font-medium text-gray-700">
                Enter backup code:
              </label>
              <input
                id="backupCode"
                name="backupCode"
                type="text"
                value={backupCode}
                onChange={(e) => setBackupCode(e.target.value.toUpperCase())}
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary focus:border-primary sm:text-sm text-center font-mono"
                placeholder="ABCD1234"
                autoComplete="off"
              />
              {errors.backupCode && (
                <p className="mt-1 text-sm text-red-600">{errors.backupCode}</p>
              )}
            </div>
          )}

          <div className="flex items-center justify-between">
            <button
              type="button"
              onClick={toggleCodeType}
              className="text-sm text-primary hover:text-primary-dark"
            >
              {useBackupCode ? 'Use authenticator app instead' : 'Use backup code instead'}
            </button>
            
            <button
              type="button"
              onClick={handleResendCode}
              className="text-sm text-primary hover:text-primary-dark"
            >
              Refresh code
            </button>
          </div>

          <Button
            type="submit"
            disabled={isLoading || (!verificationCode && !backupCode)}
            className="w-full"
          >
            {isLoading ? 'Verifying...' : 'Verify & Sign In'}
          </Button>
        </form>

        <div className="text-center space-y-2">
          <Link
            to="/login"
            className="text-sm text-primary hover:text-primary-dark"
          >
            ← Back to Sign In
          </Link>
          
          <div className="text-xs text-gray-500">
            Having trouble? Contact support if you&apos;ve lost access to your authenticator app.
          </div>
        </div>
      </div>
    </div>
  );
};

export default MFAVerification;
